import CategoryTag from "core/tags/category-tag/CategoryTag";
import InputSearchPopup from "./InputSearchPopup";
import PropTypes from "prop-types";
import _ from "lodash";
import { FormattedMessage, useIntl } from "react-intl";
import { SEARCH_TYPE } from "constants/constants";
import { useRef, useState, useEffect } from "react";
import { ErrorPopup } from "components/alert-popup/ErrorPopup";
import ErrorPopupBtn from "components/alert-popup/ErrorPopupBtn";

const InputSearchTag = ({
  errors = null,
  label = null,
  limitChoice = 1,
  minLength = 0,
  onDelete = null,
  onTagClick,
  options = [],
  placeholder = null,
  popup,
  readOnly = false,
  // Inert because a prerequisite is missing, not because the record is in
  // consultation. Distinct from `readOnly`, which replaces an empty field with
  // "-": here the input stays on screen so its placeholder can say what to do.
  disabled = false,
  required = false,
  selectedOptions = [],
  setValue,
  type = "",
  getOptions = null,
  id,
  isBo = false,
  showSearchIcon = false,
  loadDybList,
  filters,
  dataTestId,
  clearAllLabel,
  exclusiveValue,
  // Opt-in: the caller's `getOptions` already filtered server-side.
  serverFiltered = false,
  ...props
}) => {
  const inputRef = useRef(null);
  const intl = useIntl();
  const searchedTerm = useRef("");
  const [inputSearch, setInputSearch] = useState("");
  const [isOpened, setIsOpened] = useState(false);

  const [showErrorPopup, setShowErrorPopup] = useState(false);

  const getTagClickHandler = (option) => {
    // A field held inert by a missing prerequisite has no clickable tag either,
    // `onTagClick` included. `readOnly` is deliberately not covered: there the
    // tag stays a link to the record it names.
    if (disabled && !readOnly) {
      return undefined;
    }
    if (onTagClick) {
      return (event) => {
        // Keep the click on the tag from bubbling up to the container,
        // which would reopen the search popup.
        event.stopPropagation();
        onTagClick(option);
      };
    }
    if (!readOnly) {
      return () => setIsOpened(true);
    }
    return undefined;
  };

  const onChange = (e) => {
    const newValue = e.target.value;
    setInputSearch(newValue);
    if (minLength === 0 || newValue?.length > minLength) {
      setIsOpened(true);
    } else {
      setIsOpened(false);
    }
  };

  useEffect(() => {
    if (minLength > 0 && inputSearch?.length > minLength) {
      searchedTerm.current = inputSearch;
      filters
        ? getOptions({ ...filters, names: inputSearch })
        : getOptions(inputSearch);
    }
  }, [inputSearch]);

  // Server-filtered options describe `searchedTerm`, not what is typed now, and the
  // search is not re-run below `minLength`: hide them until the two match again.
  const popupOptions =
    serverFiltered && inputSearch !== searchedTerm.current ? [] : options;

  return (
    <>
      {isOpened && (
        <button
          className="input-search-close button-style"
          onClick={() => setIsOpened(!isOpened)}
        />
      )}

      <div
        className={`input-search ${isBo ? "is-bo" : ""} ${
          isBo && errors?.isMissingValue ? "is-bo-error" : ""
        }  `}
        ref={inputRef}
        onClick={
          !readOnly && !disabled && !isOpened
            ? () => setIsOpened(true)
            : undefined
        }
      >
        <div
          className="overlap-group"
          style={{ flexDirection: label ? "column" : "unset" }}
        >
          {label && (
            <label
              className={`label sourcesanspro-normal-shadow-blue-14px ${
                errors?.type || errors?.message
                  ? "label-error"
                  : "label-standard"
              }`}
            >
              <FormattedMessage {...label} />
              {required && "*"}
              {(errors?.type || errors?.message || props?.errorMessage) && (
                <ErrorPopupBtn setShowErrorPopup={setShowErrorPopup} />
              )}
            </label>
          )}
          <div
            className="display-inputs-flex sourcesanspro-normal-gray-suit-18px"
            style={isOpened ? { display: "none" } : undefined}
          >
            {!selectedOptions?.length && readOnly
              ? "-"
              : _.chain(selectedOptions)
                  .take(limitChoice || 1)
                  .map((e, index) => (
                    <CategoryTag
                      key={`${e?.value ?? "no-value"}-${index}`}
                      label={e.label}
                      showing={true}
                      many={selectedOptions?.length > 1}
                      deleteOption={false}
                      onClick={getTagClickHandler(e)}
                      color={e?.color}
                      type={type || e?.type}
                      dataTestId={dataTestId}
                      linkValue={readOnly ? e?.value : undefined}
                      profile={readOnly ? e?.profile : undefined}
                    />
                  ))
                  .value()}
            {selectedOptions?.length < limitChoice && !readOnly && (
              <input
                type="text"
                className={`${
                  showSearchIcon ? "input-search-icon" : "input-placeholder "
                }  sourcesanspro-normal-gray-suit-18px`}
                value={inputSearch}
                placeholder={
                  placeholder && selectedOptions?.length === 0
                    ? intl.formatMessage(placeholder)
                    : ""
                }
                onClick={() => !minLength && !disabled && setIsOpened(true)}
                onChange={onChange}
                disabled={readOnly || disabled}
                {...(selectedOptions?.length > 0 && {
                  style: { width: "30px" },
                })}
                id={`input-search-tag-${id}`}
                data-testid={dataTestId}
              />
            )}
          </div>
        </div>
      </div>
      {isOpened && (
        <InputSearchPopup
          defaultOptions={popupOptions}
          title={popup.title}
          placeholder={
            placeholder ?? {
              id: "input_search_popup_default_placeholder",
              defaultMessage: "Rechercher...",
            }
          }
          inputValue={inputSearch}
          setInputValue={setInputSearch}
          limit={limitChoice}
          selectedOptions={selectedOptions}
          getSelectedOptions={(sel) => {
            const removed = (selectedOptions || []).filter(
              (prev) => !sel.find((s) => s.value === prev.value)
            );
            removed.forEach((r) => onDelete?.(r.value));
            setInputSearch("");
            setValue(sel);
          }}
          readOnly={readOnly}
          searchType={SEARCH_TYPE.TAG}
          hide={() => setIsOpened(!isOpened)}
          type={type}
          loadDybList={loadDybList}
          id={id}
          clearAllLabel={clearAllLabel}
          exclusiveValue={exclusiveValue}
          serverFiltered={serverFiltered}
        />
      )}
      {showErrorPopup && (
        <ErrorPopup
          errors={props.errors}
          showErrorPopup={showErrorPopup}
          setShowErrorPopup={setShowErrorPopup}
          label={label}
          errorMessage={props?.errorMessage}
          errorValue={props?.errorValue}
        />
      )}
    </>
  );
};

InputSearchTag.propTypes = {
  errors: PropTypes.any,
  label: PropTypes.object,
  limitChoice: PropTypes.number,
  minLength: PropTypes.number,
  onDelete: PropTypes.func,
  onTagClick: PropTypes.func,
  options: PropTypes.array,
  placeholder: PropTypes.any,
  popup: PropTypes.any.isRequired,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  selectedOptions: PropTypes.any,
  setValue: PropTypes.func.isRequired,
  type: PropTypes.string,
  getOptions: PropTypes.func,
  id: PropTypes.string,
  isBo: PropTypes.bool,
  showSearchIcon: PropTypes.bool,
  loadDybList: PropTypes.func,
  errorMessage: PropTypes.string,
  errorValue: PropTypes.string,
  filters: PropTypes.array,
  clearAllLabel: PropTypes.string,
  exclusiveValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serverFiltered: PropTypes.bool,
};
export default InputSearchTag;
